import * as DateFns from 'date-fns';

export function dateFormat(date: Date, formatString: string) {
  return DateFns.format(date, formatString);
}

export function isSameDate(date1: Date, date2: Date) {
  return DateFns.isSameDay(date1, date2);
}

export function isSameYearAndMonth(date1: Date, date2: Date) {
  return DateFns.isSameYear(date1, date2) && DateFns.isSameMonth(date1, date2);
}

export function isDateSameWeek(date1: Date, date2: Date) {
  return DateFns.isSameWeek(date1, date2);
}

export interface DateDurationOptions {
  years?: number;
  months?: number;
  weeks?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}

export function addDate(date: Date, duration: DateDurationOptions) {
  return DateFns.add(date, duration);
}

export function subDate(date: Date, duration: DateDurationOptions) {
  return DateFns.sub(date, duration);
}

export function parseDate(dateString: string, formatString: string) {
  return DateFns.parse(dateString, formatString, new Date());
}

export function parseDateISO(dateString: string) {
  return DateFns.parseISO(dateString);
}

export function formatDateISO(date: Date) {
  return DateFns.formatISO(date);
}

export function intervalDuration(start: Date, end: Date) {
  return DateFns.intervalToDuration({start, end});
}

export function monthOfInterval(start: Date, end: Date) {
  return DateFns.eachMonthOfInterval({start, end});
}

export function dayOfInterval(start: Date, end: Date) {
  return DateFns.eachDayOfInterval({start, end});
}

export function weekOfInterval(start: Date, end: Date) {
  return DateFns.eachWeekOfInterval({start, end});
}

export function startDateOfDay(date: Date) {
  return DateFns.startOfDay(date);
}

export function startDateOfWeek(date: Date) {
  return DateFns.startOfWeek(date);
}

export function lastDateOfWeek(date: Date) {
  return DateFns.lastDayOfWeek(date);
}

export function getWeekIndexOfMonth(date: Date) {
  return DateFns.getWeekOfMonth(date);
}

export function startDateOfMonth(date: Date) {
  return DateFns.startOfMonth(date);
}

export function endDateOfMonth(date: Date) {
  return DateFns.endOfMonth(date);
}

export function differenceInCalendarWeek(date1: Date, date2: Date) {
  return DateFns.differenceInCalendarWeeks(date1, date2);
}

interface DifferenceInDayOptions {
  includeReferenceDate?: boolean;
}

export function differenceInDay(date1: Date, date2: Date, differenceInDayOptions?: DifferenceInDayOptions) {
  const differenceDays = DateFns.differenceInDays(DateFns.startOfDay(date1), DateFns.startOfDay(date2));
  return differenceInDayOptions?.includeReferenceDate ? differenceDays + 1 : differenceDays;
}

export function isDateFormat(date: Date) {
  return DateFns.isDate(date);
}

export function setDay(date: Date, dayOfMonth: number) {
  return DateFns.setDate(date, dayOfMonth);
}

export function parseDateWithFormat(date: Date | string | undefined | null) {
  if (!date) {
    return null;
  }
  if (typeof date === 'string') {
    const dateString = date.replace(/[-:T+]/g, '').slice(0, 14);
    switch (dateString.length) {
      case 6:
        return DateFns.parse(dateString, 'yyyyMM', new Date());
      case 8:
        return DateFns.parse(dateString, 'yyyyMMdd', new Date());
      case 12:
        return DateFns.parse(dateString, 'yyyyMMddHHmm', new Date());
      case 14:
        return DateFns.parse(dateString, 'yyyyMMddHHmmss', new Date());
      default:
        const parsedDate = DateFns.parseISO(date);
        return DateFns.isValid(parsedDate) ? parsedDate : null;
    }
  }
  return date;
}

export function isValidDate(dateString: string | null | undefined) {
  if (!dateString) {
    return false;
  }
  const [year, month, day] = dateString.split('-').map((v) => parseInt(v, 10));

  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}
